/**
 * Presence semantics (FLY3D-1029). Pure, store-free, so it can be unit tested
 * and reused anywhere without dragging in MQTT or Pinia.
 *
 * The graded states replace a boolean "online?", which is what made the
 * original bug possible: a probe that failed to get an answer and a pilot who
 * had genuinely left produced the same `false`, and the UI said "not online"
 * for both.
 */

/**
 * `unknown` and `stale` are the load-bearing values here. Both mean "we do not
 * know", and neither may ever be presented to an operator as the current pilot
 * being offline.
 */
export type PresenceState = 'active' | 'idle' | 'stale' | 'offline' | 'unknown'

export interface UserPresence {
  userID: string
  state: PresenceState
  lastSeenAt?: string | null
  lastConnectedAt?: string | null
  lastDisconnectedAt?: string | null
  disconnectReason?: string | null
  clientInitiatedDisconnect?: boolean | null
  connectionCount?: number
  activeDeviceGroupIds?: string[]
  isFlying?: boolean | null
}

/** How often this browser publishes its heartbeat. Matches the backend. */
export const HEARTBEAT_INTERVAL_MS = 20_000

/**
 * States in which the operator's dashboard is up and a takeover request will
 * actually reach them.
 */
export const REACHABLE_STATES: PresenceState[] = ['active', 'idle']

/**
 * The only states that justify offering a force takeover without first waiting
 * for a reply. Deliberately excludes `stale` and `unknown`: uncertainty is not
 * absence, and treating it as absence is the bug this ticket fixes.
 */
export const CONFIRMED_ABSENT_STATES: PresenceState[] = ['offline']

export const isReachable = (state: PresenceState): boolean =>
  REACHABLE_STATES.includes(state)

export const isConfirmedAbsent = (state: PresenceState): boolean =>
  CONFIRMED_ABSENT_STATES.includes(state)

/** Turn an AWS IoT disconnect reason into something an operator can act on. */
export function describeDisconnectReason(reason: string): string {
  switch (reason) {
    case 'CLIENT_INITIATED_DISCONNECT':
      return 'they signed out or closed the dashboard'
    case 'CONNECTION_LOST':
      return 'their network connection dropped'
    case 'MQTT_KEEP_ALIVE_TIMEOUT':
      return 'their dashboard stopped responding'
    case 'CLIENT_ERROR':
    case 'DUPLICATE_CLIENTID':
      return 'their dashboard reconnected elsewhere'
    case 'AUTH_ERROR':
      return 'their session expired'
    default:
      return reason.toLowerCase().replace(/_/g, ' ')
  }
}

export function formatLastSeen(lastSeenAt: string | null | undefined, now = Date.now()): string {
  if (!lastSeenAt) return 'at an unknown time'
  const seen = new Date(lastSeenAt).getTime()
  if (Number.isNaN(seen)) return 'at an unknown time'

  const minutes = Math.round((now - seen) / 60000)
  if (minutes < 1) return 'less than a minute ago'
  return `${minutes} minute${minutes === 1 ? '' : 's'} ago`
}

/**
 * Describe someone's presence to the operator deciding whether to take their
 * dock.
 *
 * Every branch states what we actually observed. None of them claims the
 * person is offline unless the broker reported the disconnect.
 */
export function humanisePresence(
  presence: UserPresence | undefined,
  name: string,
  now = Date.now(),
): string {
  if (!presence) return `We could not determine whether ${name} is at their station.`

  const ago = formatLastSeen(presence.lastSeenAt, now)

  switch (presence.state) {
    case 'active':
      return `${name} is at their station and active.`
    case 'idle':
      return `${name} is connected but has not interacted recently (last seen ${ago}).`
    case 'stale':
      return `${name}'s dashboard is connected but has stopped responding (last seen ${ago}). We cannot confirm whether they are at their station.`
    case 'offline':
      return `${name} is not connected. Last seen ${ago}${
        presence.disconnectReason
          ? ` (${describeDisconnectReason(presence.disconnectReason)})`
          : ''
      }.`
    case 'unknown':
    default:
      return `We have no presence information for ${name}. This is not the same as them being offline.`
  }
}

/**
 * Surface the aircraft state carried on the owner's last heartbeat.
 *
 * Force takeover is deliberately never blocked (a pilot's system may be
 * failing, which is exactly when takeover is urgent), so this informs the
 * decision rather than gating it.
 */
export function describeAircraftEvidence(presence: UserPresence): string {
  if (presence.isFlying === true) {
    return 'WARNING: their last report said an aircraft was AIRBORNE. Taking the dock now may interrupt an active flight.'
  }
  if (presence.isFlying === false) {
    return 'Their last report showed no aircraft airborne.'
  }
  return ''
}
